import { SVError } from '@service-victoria/component-library/utils/errors';
import { isEmpty } from 'lodash-es';
import React, { useContext, useEffect } from 'react';

import { useMethods } from 'app/hooks';
import { Store } from 'app/interfaces';
import { constants, storage } from 'app/utils';

import actions from './actions';
import { TransactionType } from 'app/interfaces/common';
import { v4 as uuid } from 'uuid';

/**
 * Ref:
 * https://levelup.gitconnected.com/implementing-redux-style-state-management-with-reacts-usecontext-usereducer-hooks-c1c5596d9619
 * https://medium.com/@akrush95/global-cached-state-in-react-using-hooks-context-and-local-storage-166eacf8ab46
 * https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
 * https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
 */

const AppContext = React.createContext({
  currentState: storage.getStorage<Store.State>(constants.ProviderStorageKeys.State),
  actions: {} as Store.Actions
});

function useAppState(): readonly [Store.State] {
  const initialState: Store.State = {
    transactions: {
      [TransactionType.ProviderDetails]: {
        ID: uuid()
      },
      [TransactionType.Redemption]: {
        ID: uuid()
      }
    }
  };
  return [initialState] as const;
}

export type AppProviderProps = {
  /**
   * Optional unique id to identify it in the Redux dev tools. If no id is supplied, the hook won't be connected to dev tools
   */
  id?: string;
  /**
   * Optional initial state to launch the context provider with
   */
  state?: Store.State;
};

/**
 * Get the SideNav context provider with the different state data used across the entire sideNav
 */
export const AppProvider: React.FC<React.PropsWithChildren<AppProviderProps>> = ({
  state,
  id,
  children
}) => {
  const [initialState] = useAppState();
  // https://github.com/pelotom/use-methods
  // https://github.com/troch/reinspect
  const [currentState, callbacks] = useMethods(
    actions,
    isEmpty(state) ? initialState : state,
    state => state ?? initialState,
    id
  );

  // Persist the new state back to the storage when it gets changed (https://github.com/immerjs/immer/issues/441#issuecomment-540044486)
  useEffect(() => {
    storage.updateStorage(
      constants.ProviderStorageKeys.State,
      JSON.parse(JSON.stringify(currentState))
    );
  }, [currentState]);

  // Cast the callbacks response from 'useMethods' function to match the interface of allowed "Callbacks"
  return (
    <AppContext.Provider value={{ currentState, actions: callbacks }}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * instead of calling useContext directly in our components,  we make our own hook that throws an error if we try to access context outside of the provider
 */
export function useAppContext(): Store.AppContext {
  const context = useContext(AppContext);
  if (!context) {
    throw new SVError('AppContext must be used with AppProvider!');
  }
  return context;
}
